import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { GlassCard } from '@/components/ui/glass-card'
import { useLuxeHero, useLuxeReveal, useLuxeGrid } from '@gjl/motion/css'
import { ArrowRight, MapPin, Building, TrendingUp, Shield } from 'lucide-react'

export default function StrategyPage() {
  const heroRef = useLuxeHero<HTMLDivElement>(0.2)
  const marketRef = useLuxeReveal<HTMLDivElement>('.reveal')
  const assetRef = useLuxeGrid<HTMLDivElement>('.asset-card')
  const operateRef = useLuxeGrid<HTMLDivElement>('.operate-card')
  const ctaRef = useLuxeReveal<HTMLDivElement>('.reveal')

  return (
    <div className="bg-background text-foreground">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="bg-gradient-dark-gold absolute inset-0" />
        <div ref={heroRef} className="relative z-10 mx-auto max-w-4xl px-4 text-center">
          <p className="text-primary mb-6 text-lg font-medium tracking-wide">Our investment strategy</p>
          <h1 className="text-fluid-h1 text-foreground mb-6">Stress-tested. Long-hold. Cash-flow first.</h1>
          <p className="text-fluid-body-lg text-muted-foreground mx-auto max-w-2xl">
            Small multifamily, underwritten against adverse scenarios, held for decades. The discipline is the product.
          </p>
        </div>
      </section>

      {/* Why Cleveland Section */}
      <section ref={marketRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <GlassCard accent>
            <div className="mb-6 flex items-center gap-3">
              <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-xl">
                <MapPin className="text-primary h-6 w-6" />
              </div>
              <h2 className="text-fluid-h3 text-foreground">Why Cleveland? (And Why That Matters to You)</h2>
            </div>

            <div className="reveal text-muted-foreground space-y-6">
              <p className="text-fluid-body-lg">
                When I started researching markets, everyone was talking about the same cities: Austin, Nashville,
                Boise. Great markets - but everyone knows it. High prices, low yields, thousands of investors competing
                for the same deals.
              </p>
              <p className="text-fluid-body-lg">
                Then I looked at Cleveland. A city most coastal investors dismiss without a second thought. But the
                numbers told a different story.
              </p>
              <p className="text-fluid-body-lg">
                While others chase appreciation in hot markets, we underwrite Cleveland deals to 8-12% cash-on-cash
                returns from day one. While others hope for rent growth, we buy properties where current rents already
                cover expenses with room to spare.
              </p>
              <p className="text-foreground text-xl font-semibold">
                Cleveland rewards disciplined, long-hold investing. The market does the compounding while underwriting
                carries the risk.
              </p>
            </div>
          </GlassCard>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Market Focus Section */}
      <section className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="mb-12 text-center">
            <h2 className="text-fluid-h2 text-foreground mb-4">Market Focus: Overlooked = Opportunity</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
              We specialize in Cleveland, Ohio - a stable, cash-flowing market where fundamentals matter more than hype.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <GlassCard className="gold-accent-left pl-8" hover>
              <h3 className="text-foreground mb-3 text-xl font-semibold">Cleveland, Ohio</h3>
              <p className="text-muted-foreground">
                Stable population, growing economy, affordable properties. Our first deal? An occupied duplex in West
                Boulevard generating cash flow from day one.
              </p>
            </GlassCard>

            <GlassCard className="gold-accent-left pl-8" hover>
              <h3 className="text-foreground mb-3 text-xl font-semibold">Working-Class Neighborhoods</h3>
              <p className="text-muted-foreground">
                We target submarkets where working families need quality housing - strong rent-to-price ratios,
                consistent demand, minimal vacancy risk.
              </p>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Asset Type Section */}
      <section ref={assetRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="mb-12 text-center">
            <h2 className="text-fluid-h2 text-foreground mb-4">Asset Type: Small Multifamily</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
              We focus on 2-12 unit residential buildings - small enough to understand deeply, large enough to scale.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <GlassCard className="asset-card text-center" hover>
              <div className="bg-primary/10 mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-xl">
                <Building className="text-primary h-8 w-8" />
              </div>
              <h3 className="text-foreground mb-2 text-xl font-semibold">Duplexes</h3>
              <p className="text-muted-foreground">Two units, residential financing, manageable operations</p>
            </GlassCard>

            <GlassCard className="asset-card text-center" hover>
              <div className="bg-primary/10 mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-xl">
                <Building className="text-primary h-8 w-8" />
              </div>
              <h3 className="text-foreground mb-2 text-xl font-semibold">Triplexes</h3>
              <p className="text-muted-foreground">Three units, strong cash flow, still residential financing</p>
            </GlassCard>

            <GlassCard className="asset-card text-center" hover>
              <div className="bg-primary/10 mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-xl">
                <Building className="text-primary h-8 w-8" />
              </div>
              <h3 className="text-foreground mb-2 text-xl font-semibold">4-Plexes & Beyond</h3>
              <p className="text-muted-foreground">More doors, better economies of scale, commercial lending</p>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* How We Operate Section */}
      <section ref={operateRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="mb-12 text-center">
            <h2 className="text-fluid-h2 text-foreground mb-4">How We Operate: Patient Capital</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
              Our operating philosophy emphasizes stability over speculation.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <GlassCard className="operate-card" hover>
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl">
                  <TrendingUp className="text-primary h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-foreground mb-2 text-xl font-semibold">Buy and Hold Forever</h3>
                  <p className="text-muted-foreground">
                    We use conservative leverage and plan to hold properties for decades. No quick flips, no market
                    timing.
                  </p>
                </div>
              </div>
            </GlassCard>

            <GlassCard className="operate-card" hover>
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl">
                  <Shield className="text-primary h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-foreground mb-2 text-xl font-semibold">Light-to-Moderate Rehab</h3>
                  <p className="text-muted-foreground">
                    Fix what's broken, improve what matters. No speculative renovations or over-improvements.
                  </p>
                </div>
              </div>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <GlassCard accent className="reveal text-center">
            <p className="text-muted-foreground mb-6 text-lg">
              This strategy isn't for everyone. It's for patient investors who want real assets generating real income.
            </p>
            <Link to="/invest">
              <Button size="xl">
                Learn About Partnering With Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </GlassCard>
        </div>
      </section>
    </div>
  )
}
